import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { faqItems, faqCategories } from '@/mocks/faq';

function FaqPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [helpful, setHelpful] = useState<Record<string, boolean>>({});
  const [notHelpful, setNotHelpful] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return faqItems.filter((item) => {
      const matchesQuery =
        searchQuery.trim() === '' ||
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
      return matchesQuery && matchesCategory;
    });
  }, [searchQuery, activeCategory]);

  const markHelpful = (id: string) => {
    setHelpful((prev) => ({ ...prev, [id]: true }));
    setNotHelpful(null);
  };

  const markNotHelpful = (id: string) => {
    setHelpful((prev) => ({ ...prev, [id]: false }));
    setNotHelpful(id);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-10 md:pb-14">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
              Centre d'aide
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-50 mb-4">
              Comment pouvons-nous vous aider ?
            </h1>
            <p className="text-base md:text-lg text-foreground-400 max-w-2xl mx-auto mb-8">
              Trouvez rapidement des réponses à vos questions sur l'écosystème WorldApeiron.
            </p>

            <form onSubmit={(e) => e.preventDefault()} className="max-w-xl mx-auto">
              <div className="flex items-center bg-background-100/80 backdrop-blur-md border border-background-300/60 rounded-lg overflow-hidden">
                <div className="w-12 h-12 flex items-center justify-center shrink-0">
                  <i className="ri-search-line text-lg text-foreground-500" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher une question..."
                  className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-3 outline-none"
                />
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-8">
        <div className="flex flex-wrap justify-center gap-2">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${
              activeCategory === 'all'
                ? 'bg-primary-500 text-foreground-950 border-primary-500'
                : 'bg-background-100 text-foreground-500 border-background-300/60 hover:border-primary-400/40'
            }`}
          >
            Toutes les catégories
          </button>
          {faqCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-primary-500 text-foreground-950 border-primary-500'
                  : 'bg-background-100 text-foreground-500 border-background-300/60 hover:border-primary-400/40'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* FAQ items */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-16">
        <div className="max-w-3xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-question-line text-2xl text-foreground-400" />
              </div>
              <p className="text-foreground-500 font-medium mb-2">Aucune question ne correspond à votre recherche</p>
              <p className="text-sm text-foreground-500">Essayez avec des mots-clés différents.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((item) => {
                const isOpen = openItem === item.id;
                return (
                  <div
                    key={item.id}
                    className="bg-background-100 border border-background-200/70 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenItem(isOpen ? null : item.id)}
                      className="w-full flex items-center justify-between gap-4 p-4 md:p-5 text-left"
                    >
                      <span className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-primary-400 bg-primary-500/10 px-2.5 py-1 rounded-full shrink-0 whitespace-nowrap">
                          {item.category}
                        </span>
                        <span className="font-heading font-semibold text-sm md:text-base text-foreground-50">
                          {item.question}
                        </span>
                      </span>
                      <span className="w-6 h-6 flex items-center justify-center shrink-0">
                        <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </span>
                    </button>

                    {isOpen && (
                      <div className="px-4 md:px-5 pb-5">
                        <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-4">
                          {item.answer}
                        </p>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-4 border-t border-background-200/70">
                          <span className="text-sm text-foreground-400">Cette réponse vous a-t-elle aidé ?</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => markHelpful(item.id)}
                              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap ${
                                helpful[item.id] === true
                                  ? 'bg-secondary-500 text-foreground-950 border-secondary-500'
                                  : 'bg-background-50 text-foreground-500 border-background-300/60 hover:border-secondary-400/40'
                              }`}
                            >
                              <i className="ri-thumb-up-line mr-1" />
                              Oui
                            </button>
                            <button
                              onClick={() => markNotHelpful(item.id)}
                              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap ${
                                helpful[item.id] === false
                                  ? 'bg-foreground-800 text-foreground-100 border-foreground-800'
                                  : 'bg-background-50 text-foreground-500 border-background-300/60 hover:border-foreground-400/40'
                              }`}
                            >
                              <i className="ri-thumb-down-line mr-1" />
                              Non
                            </button>
                          </div>
                        </div>

                        {notHelpful === item.id && (
                          <div className="mt-4 bg-background-50 border border-background-300/60 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
                            <p className="text-sm text-foreground-500">
                              Désolé que cette réponse ne vous ait pas aidé. Notre équipe Support est là pour vous.
                            </p>
                            <button
                              onClick={() => navigate('/support')}
                              className="px-4 py-2 text-xs font-semibold bg-primary-500 text-foreground-950 rounded-md hover:bg-primary-400 transition-colors whitespace-nowrap"
                            >
                              Contacter le Support
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-20">
        <div className="max-w-3xl mx-auto bg-background-100 border border-background-200/70 rounded-2xl p-8 md:p-10 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-4">
            <i className="ri-customer-service-2-line text-2xl text-primary-400" />
          </div>
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-3">
            Vous n'avez pas trouvé votre réponse ?
          </h2>
          <p className="text-foreground-400 max-w-md mx-auto mb-6">
            Notre équipe de support est disponible pour vous aider avec n'importe quelle question.
          </p>
          <button
            onClick={() => navigate('/support')}
            className="px-8 py-3.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors"
          >
            Contacter le Support
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default FaqPage;